import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { DischargeChecklistEntry } from 'models/DischargeChecklistItem';
import colors from 'styles/theme/colors';

type Props = {
  item: DischargeChecklistEntry;
  saving: boolean;
  onSubmit: (text: string) => void;
  onClose: () => void;
};

// Adds an item's note, or replaces it ("Update Note") when it has one. Notes are required and have no
// length limit.
export default function DischargeChecklistNoteModal({ item, saving, onSubmit, onClose }: Props) {
  const [text, setText] = useState(item.note?.text ?? '');
  const updating = !!item.note;

  return (
    <StandardModal title={updating ? 'Update Note' : 'Add Note'} onCancel={onClose}>
      <StandardModal.RecordName>Item: {item.label}</StandardModal.RecordName>
      <Content>
        <Label htmlFor='discharge-checklist-note'>Note</Label>
        <TextArea
          id='discharge-checklist-note'
          value={text}
          onChange={(event) => setText(event.target.value)}
          data-testid='discharge-checklist-note-input'
        />
      </Content>
      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(text.trim())}
          disabled={!text.trim()}
          loading={saving}
          data-testid='discharge-checklist-note-submit'>
          Submit
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 32px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.black75};
`;

const TextArea = styled.textarea`
  border: 1px solid ${colors.black25};
  border-radius: 4px;
  font-size: 14px;
  line-height: 20px;
  min-height: 96px;
  padding: 12px 16px;
  resize: vertical;
  width: 100%;
  outline: none;
  background-color: transparent;
`;
